class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.cachedResults = {};
  }

  connectedCallback() {
    this.input.addEventListener(
      'input',
      this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.input.addEventListener('focus', this.onFocus.bind(this));

    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();
    this.searchTerm = searchTerm;

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFocus() {
    const searchTerm = this.input.value.trim();
    if (searchTerm.length && this.predictiveSearchResults.innerHTML.trim()) {
      this.open();
    }
  }

  getSearchResults(searchTerm) {
    // Check cache first
    if (this.cachedResults[searchTerm]) {
      this.renderSearchResults(this.cachedResults[searchTerm]);
      return;
    }

    this.setLoading(true);

    this.abortController.abort();
    this.abortController = new AbortController();

    fetch(
      `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(
        searchTerm
      )}&resources[type]=product,query&resources[limit]=4&resources[options][fields]=title,product_type,variants.title,vendor`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.json();
      })
      .then((data) => {
        // Cache the result
        this.cachedResults[searchTerm] = data;
        this.renderSearchResults(data);
      })
      .catch((error) => {
        if (error?.code === 20) return; // aborted
        this.close();
        throw error;
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  setLoading(isLoading) {
    const loadingState = this.querySelector('.predictive-search__loading-state');
    if (loadingState) {
      loadingState.style.display = isLoading ? 'flex' : 'none';
    }
    if (isLoading) {
      this.setAttribute('loading', '');
    } else {
      this.removeAttribute('loading');
    }
  }

  renderSearchResults(data) {
    const resources = data.resources?.results;
    if (!resources) {
      this.close();
      return;
    }

    const queries = resources.queries || [];
    const products = resources.products || [];

    if (!queries.length && !products.length) {
      this.renderNoResults();
      this.open();
      return;
    }

    const formAction = this.querySelector('form')?.getAttribute('action') || '/search';
    const searchTerm = this.input.value.trim();
    const searchUrl = `${formAction}?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last`;

    let html = '<div class="predictive-search__results-wrapper">';

    // Suggestions column
    if (queries.length) {
      html += '<div class="predictive-search__suggestions">';
      html += '<h3 class="predictive-search__heading">SUGGESTIONS</h3>';
      html += '<ul class="predictive-search__suggestions-list" role="listbox">';
      queries.forEach((query) => {
        html += `
          <li class="predictive-search__suggestion" role="option">
            <a href="${formAction}?q=${encodeURIComponent(query.text)}&options%5Bprefix%5D=last" class="predictive-search__suggestion-link">
              ${this.highlightQuery(query.styled_text)}
            </a>
          </li>`;
      });
      html += '</ul>';
      html += '</div>';
    }

    // Products column
    if (products.length) {
      html += '<div class="predictive-search__products">';
      html += '<h3 class="predictive-search__heading">PRODUCTS</h3>';
      html += '<div class="predictive-search__products-grid">';
      products.forEach((product) => {
        const image = product.image || product.featured_image?.url || null;
        let imageUrl = '';
        if (image) {
          // Shopify CDN URLs support size params: append &width=300 or use _300x suffix
          imageUrl = typeof image === 'string' ? image : '';
          if (imageUrl.includes('/cdn/') && imageUrl.includes('?')) {
            imageUrl += '&width=300';
          } else if (imageUrl.includes('/cdn/')) {
            imageUrl += '?width=300';
          }
        }
        const rawPrice = product.price || product.price_min || '0';
        const price = this.formatMoney(rawPrice);
        const rawComparePrice = product.compare_at_price_max || product.compare_at_price || null;
        const comparePrice =
          rawComparePrice && parseFloat(rawComparePrice) > parseFloat(rawPrice)
            ? this.formatMoney(rawComparePrice)
            : null;

        html += `
          <a href="${product.url}" class="predictive-search__product-card">
            <div class="predictive-search__product-image-wrapper">
              ${
                imageUrl
                  ? `<img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" class="predictive-search__product-image" loading="lazy" width="150" height="150">`
                  : '<div class="predictive-search__product-image-placeholder"></div>'
              }
              ${!product.available ? '<span class="predictive-search__sold-out-badge">Sold Out</span>' : ''}
            </div>
            <div class="predictive-search__product-info">
              <span class="predictive-search__product-title">${this.escapeHtml(product.title)}</span>
              <span class="predictive-search__product-price">
                ${comparePrice ? `<s class="predictive-search__compare-price">${comparePrice}</s>` : ''}
                ${price}
              </span>
            </div>
          </a>`;
      });
      html += '</div>';
      html += '</div>';
    }

    html += '</div>';

    // View all results
    html += `
      <div class="predictive-search__view-all">
        <a href="${searchUrl}" class="predictive-search__view-all-link">VIEW ALL RESULTS</a>
      </div>`;

    this.predictiveSearchResults.innerHTML = html;
    this.open();
  }

  renderNoResults() {
    this.predictiveSearchResults.innerHTML = `
      <div class="predictive-search__no-results">
        <p>No results found for &ldquo;${this.escapeHtml(this.searchTerm)}&rdquo;</p>
      </div>`;
  }

  highlightQuery(styledText) {
    // Shopify returns styled_text with <mark> tags, pass through
    return styledText || '';
  }

  formatMoney(cents) {
    if (cents == null) return '';
    // Shopify suggest API returns prices as strings like "15.00" (dollars) or "1500" (cents)
    let amount;
    const str = String(cents);
    if (str.includes('.')) {
      amount = parseFloat(str).toFixed(2);
    } else {
      amount = (parseInt(str, 10) / 100).toFixed(2);
    }
    // Use the shop's money format if available, otherwise fall back to USD
    return '$' + amount + ' USD';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.predictiveSearchResults.style.display = 'block';
    this.input.setAttribute('aria-expanded', 'true');
    this.classList.add('is-active');
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.predictiveSearchResults.style.display = 'none';
    this.input.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-active');
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);
